import type { RestoreRefusal } from '@shared/types';
import type { RecordType } from './router';

// Entry points one product area offers the others: writing an email, closing a deal, merging records and so on.
// Each area registers its implementations when it loads; callers use `actions.x(...)` without importing that area.

export interface ComposeRecipient {
  personId?: string;
  email: string;
  name?: string;
}

export interface ComposeRequest {
  /** A reply or forward continues an existing conversation. */
  mode?: 'new' | 'reply' | 'replyAll' | 'forward';
  threadId?: string;
  /** The message being replied to or forwarded; defaults to the latest in the thread. */
  emailId?: string;
  to?: ComposeRecipient[];
  /** Records the email is filed to. */
  personId?: string;
  companyId?: string;
  dealId?: string;
  subject?: string;
  body?: string;
  templateId?: string;
  /** Reopens a scheduled email in the composer to change it. */
  outboxId?: string;
}

export interface ListSendRequest {
  personIds: string[];
  /** Name of the list or saved view the people came from, shown in the dialog title. */
  listName?: string;
}

export interface FollowupInit {
  personId?: string;
  companyId?: string;
  dealId?: string;
  title?: string;
}

export interface AppActions {
  /** Opens the composer. With no request it starts a blank email. */
  compose(req?: ComposeRequest): void;
  /** Sends one email as personal copies to many people. */
  listSend(req: ListSendRequest): void;
  /** Starts closing a deal, which asks for the reason. */
  closeDeal(dealId: string, outcome: 'won' | 'lost'): void;
  newDeal(init?: { companyId?: string; personId?: string; stageId?: string }): void;
  /** Shows a deal: its record, scrolled to the deal. */
  openDeal(dealId: string): void;
  newRecord(type: RecordType, init?: { companyId?: string; name?: string }): void;
  /** Compares two or more records side by side and merges them into one. */
  merge(type: RecordType, ids: string[]): void;
  /** Lists people or companies that stayed in the bin as duplicates, offering to restore each by merging. */
  restoreDuplicates(type: RecordType, refused: RestoreRefusal[]): void;
  addFollowup(init?: FollowupInit): void;
  /** Starts an AI Finder search from a description. */
  finderAsk(description: string): void;
  backupNow(): void;
  /** Shows an email conversation in the Inbox. */
  openThread(threadId: string): void;
  /** Shows an email sent from Clientbook: in Scheduled while it waits, in its conversation once it went out. */
  openOutbox(outboxId: string): void;
  /** Shows an AI Finder search and its suggestions. */
  openFinder(searchId: string): void;
  /** Asks, then looks up the key people at the chosen companies with AI; the people found wait on each company. */
  findKeyPeople(companyIds: string[]): void;
  /** Opens what the team offers and the capabilities and markets companies are grouped by. */
  profileTerms(section?: 'offerings' | 'capabilities' | 'markets'): void;
}

const registered: Partial<AppActions> = {};

export function registerActions(impl: Partial<AppActions>): void {
  Object.assign(registered, impl);
}

export const actions = new Proxy({} as AppActions, {
  get(_target, name: string) {
    return (...args: unknown[]) => {
      const fn = registered[name as keyof AppActions] as ((...a: unknown[]) => void) | undefined;
      if (!fn) {
        console.error(`[actions] "${name}" is not registered`);
        return;
      }
      fn(...args);
    };
  },
});
